<template>
  <div class="main-container">
    <Header />

    <main>
      <Hero />

      <section
        id="about"
        data-analytics-section="capabilities"
        data-analytics-section-name="Capabilities"
        class="capabilities-section section-space--pt_90 section-space--pb_90"
        aria-labelledby="capabilities-title"
      >
        <div class="container">
          <div class="home-section-heading capabilities-heading text-center">
            <p class="section-kicker">One controlled workflow</p>
            <h2 id="capabilities-title" class="home-heading">
              After Effects stays in charge. The handoff gets simpler.
            </h2>
            <p class="home-lead">
              You define the editable controls. Your team supplies the content. MoDeck
              connects the two.
            </p>
          </div>
        </div>

        <div class="container intro-video-action">
          <button
            type="button"
            class="intro-video-button"
            aria-label="Watch the MoDeck intro video"
            @click="opensilentbox('https://www.youtube.com/watch?v=JKWOJZWDnlc')"
          >
            <span class="intro-video-icon" aria-hidden="true">
              <i class="fas fa-play"></i>
            </span>
            <span>Watch intro video</span>
          </button>
        </div>

        <FeatureList />
      </section>

      <section
        id="use-cases"
        data-analytics-section="use_cases"
        data-analytics-section-name="Use cases"
        class="use-cases-section section-space--pt_90 section-space--pb_90"
        aria-labelledby="use-cases-title"
      >
        <div class="container">
          <div class="home-section-heading text-center section-space--mb_60">
            <p class="section-kicker">Where MoDeck shines</p>
            <h2 id="use-cases-title" class="home-heading">The jobs it was built for.</h2>
          </div>

          <div class="row use-case-grid">
            <article class="col-md-6 col-lg-3 use-case-item">
              <p class="use-case-label">Campaigns</p>
              <h3>Social and campaign variants</h3>
              <p>Swap products, offers, names, dates, and approved media.</p>
            </article>
            <article class="col-md-6 col-lg-3 use-case-item">
              <p class="use-case-label">Localization</p>
              <h3>Language and market versions</h3>
              <p>Keep the animation consistent across every market.</p>
            </article>
            <article class="col-md-6 col-lg-3 use-case-item">
              <p class="use-case-label">Data</p>
              <h3>Data-driven video batches</h3>
              <p>Turn CSV, Google Sheets, or JSON rows into videos.</p>
            </article>
            <article class="col-md-6 col-lg-3 use-case-item">
              <p class="use-case-label">Self-service</p>
              <h3>On-brand team content</h3>
              <p>Share the template without sharing the project file.</p>
            </article>
          </div>
        </div>
      </section>

      <section
        id="steps"
        data-analytics-section="how_it_works"
        data-analytics-section-name="How it works"
        class="workflow-section section-space--pt_90 section-space--pb_90"
        aria-labelledby="steps-title"
      >
        <div class="container">
          <div class="home-section-heading text-center section-space--mb_60">
            <p class="section-kicker">How it works</p>
            <h2 id="steps-title" class="home-heading">
              Set it up once.<br />Repeat the useful part.
            </h2>
          </div>
          <PaymentFeature />
          <p class="section-footnote text-center">
            Need the details?
            <router-link to="/docs" data-analytics-id="workflow_docs"
              >Read the complete setup and workflow guide.</router-link
            >
          </p>
        </div>
      </section>

      <section
        id="fit"
        data-analytics-section="best_fit"
        data-analytics-section-name="Best fit"
        class="fit-section section-space--pt_90 section-space--pb_90"
        aria-labelledby="fit-title"
      >
        <div class="container">
          <div class="home-section-heading text-center fit-heading">
            <p class="section-kicker">A precise fit</p>
            <h2 id="fit-title" class="home-heading">
              Made for repeatable video, not every kind of video.
            </h2>
            <p class="home-lead">
              MoDeck shines when the design is settled and the content changes often. It
              is a template editor and render coordinator, not a freeform editor or cloud
              render farm.
            </p>
          </div>
          <div class="fit-facts" role="list">
            <p role="listitem"><strong>Owner</strong> After Effects + desktop app</p>
            <p role="listitem"><strong>Editor</strong> Browser only</p>
            <p role="listitem"><strong>Output</strong> Rendered on your machine</p>
          </div>
          <p class="section-footnote text-center"><router-link to="/modeck-review">Explore MoDeck’s features, requirements, and limitations</router-link></p>
        </div>
      </section>

      <section
        id="faq"
        data-analytics-section="faq"
        data-analytics-section-name="Frequently asked questions"
        class="faq-banner-area section-space--pt_90 section-space--pb_90"
        aria-labelledby="faq-title"
      >
        <div class="container faq-container">
          <div class="home-section-heading text-center section-space--mb_60">
            <h2 id="faq-title" class="home-heading">Frequently asked questions</h2>
          </div>
          <div class="faq-wrapper">
            <Accordion />
          </div>
          <p class="section-footnote text-center"><router-link to="/modeck-review">Read the full official product guide</router-link></p>
        </div>
      </section>

      <CtaOne />

      <section
        id="pricing"
        data-analytics-section="pricing"
        data-analytics-section-name="Pricing"
        class="pricing-table-area section-space--pt_90 section-space--pb_90"
        aria-labelledby="pricing-title"
      >
        <div class="container pricing-container">
          <div class="home-section-heading text-center section-space--mb_60">
            <p class="section-kicker">Plans</p>
            <h2 id="pricing-title" class="home-heading">
              Get the full features of MoDeck.
            </h2>
            <p class="home-lead">
              Try the complete workflow free for 15 days, then choose the limits your team
              needs.
            </p>
          </div>
          <PricingOne />
          <p class="section-footnote text-center"><router-link to="/modeck-review">See whether MoDeck fits your team’s workflow</router-link></p>
          <p class="pricing-purchase-note text-center">
            Subscribe directly in
            <a href="https://app.modeck.io/settings?s=1" data-analytics-id="pricing_account_settings">account settings</a>, or purchase
            through
            <a
              href="https://aescripts.com/modeck/"
              target="_blank"
              rel="noopener noreferrer"
              data-analytics-id="pricing_aescripts"
              >aescripts</a
            >
            and activate the license key in your account.
          </p>
        </div>
      </section>
    </main>

    <FooterMain />
  </div>
</template>

<script>
import Header from "../components/Header";
import Hero from "../components/Hero";
import PricingOne from "../components/PricingOne";
import CtaOne from "../components/CtaOne";
import FooterMain from "../components/FooterMain";
import PaymentFeature from "../components/PaymentFeature";
import FeatureList from "../components/FeatureList";
import Accordion from "../components/Accordion";
import { trackEvent } from "../utils/analytics";

export default {
  name: "HomePage",
  components: {
    Header,
    Hero,
    FeatureList,
    PricingOne,
    PaymentFeature,
    CtaOne,
    FooterMain,
    Accordion,
  },
  methods: {
    opensilentbox(src) {
      trackEvent("video_start", {
        video_id: "landing_intro",
        video_title: "MoDeck intro video",
        placement: "capabilities",
      });
      this.$silentbox.open({ src });
    },
  },
};
</script>

<style lang="scss" scoped>
section[id] {
  scroll-margin-top: 88px;
}

.faq-banner-area,
.pricing-table-area {
  background: #ffffff;
}

.capabilities-section,
.workflow-section {
  background: #f7f9fc;
}

.use-cases-section {
  background: #ffffff;
}

.home-section-heading {
  max-width: 850px;
  margin-left: auto;
  margin-right: auto;
}

.capabilities-heading {
  margin-bottom: 28px;
}

.pricing-container {
  max-width: 1280px;
}

.section-kicker,
.use-case-label {
  margin-bottom: 5px;
  color: #d9317c;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.home-heading {
  margin-bottom: 20px;
  color: #364563;
  font-size: clamp(34px, 4vw, 52px);
  font-weight: 400;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.home-lead {
  max-width: 760px;
  margin: 0 auto;
  color: #5a6e8e;
  font-size: 18px;
  line-height: 1.75;
}

.intro-video-action {
  display: flex;
  justify-content: center;
  margin-bottom: 48px;
}

.intro-video-button {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d7e0ea;
  border-radius: 999px;
  color: #364563;
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  gap: 11px;
  padding: 9px 20px 9px 10px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.intro-video-icon {
  align-items: center;
  background: #d9317c;
  border-radius: 50%;
  color: #ffffff;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.intro-video-icon i {
  font-size: 11px;
  margin-left: 2px;
}

.intro-video-button:hover,
.intro-video-button:focus-visible {
  border-color: #68c0ce;
  box-shadow: 0 8px 24px rgba(54, 69, 99, 0.12);
  transform: translateY(-1px);
}

.intro-video-button:focus-visible {
  outline: 3px solid rgba(104, 192, 206, 0.32);
  outline-offset: 3px;
}

.use-case-grid {
  border-top: 1px solid #dfe5ed;
  border-bottom: 1px solid #dfe5ed;
}

.use-case-item {
  padding: 36px 30px 32px;
}

.use-case-item + .use-case-item {
  border-left: 1px solid #dfe5ed;
}

.use-case-item h3 {
  min-height: 56px;
  margin-bottom: 14px;
  color: #364563;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.4;
}

.use-case-item p:last-child {
  margin-bottom: 0;
  line-height: 1.8;
}

.section-footnote,
.pricing-purchase-note {
  margin: 32px auto 0;
  color: #74829a;
  font-size: 14px;
  line-height: 1.75;
}

.section-footnote a,
.pricing-purchase-note a {
  color: #d9317c;
  font-weight: 500;
}

.fit-section {
  background: #364563;
}

.fit-section .section-kicker {
  color: #68c0ce;
}

.fit-section .home-heading,
.fit-section .home-lead {
  color: #ffffff;
}

.fit-section .home-lead {
  color: rgba(255, 255, 255, 0.76);
}

.fit-heading {
  max-width: 900px;
}

.fit-facts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  max-width: 900px;
  margin: 42px auto 0;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

.fit-facts p {
  padding: 20px 24px;
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 14px;
  text-align: center;
}

.fit-facts p + p {
  border-left: 1px solid rgba(255, 255, 255, 0.16);
}

.fit-facts strong {
  display: block;
  margin-bottom: 5px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
}

.faq-container {
  max-width: 980px;
}

@media (max-width: 991px) {
  .use-case-item:nth-child(3) {
    border-left: 0;
  }

  .use-case-item:nth-child(n + 3) {
    border-top: 1px solid #dfe5ed;
  }
}

@media (max-width: 767px) {
  .use-case-item + .use-case-item {
    border-top: 1px solid #dfe5ed;
    border-left: 0;
  }

  .use-case-item h3 {
    min-height: 0;
  }
}

@media (max-width: 575px) {
  .capabilities-heading {
    margin-bottom: 22px;
  }

  .intro-video-action {
    margin-bottom: 36px;
  }

  .home-heading {
    font-size: 34px;
  }

  .home-lead {
    font-size: 17px;
  }

  .use-case-item {
    padding: 28px 24px;
  }

  .fit-facts {
    grid-template-columns: 1fr;
  }

  .fit-facts p + p {
    border-top: 1px solid rgba(255, 255, 255, 0.16);
    border-left: 0;
  }
}
</style>
